import React from 'react';
import { Subscription, SubscriptionStatus } from '../../shared/types';
import {
  formatCurrency,
  formatDate,
  formatSubscriptionStatus,
} from '../../shared/utils/formatting';

export interface SubscriptionCardProps {
  subscription: Subscription;
  onCancel?: (subscriptionId: string) => void;
  onReactivate?: (subscriptionId: string) => void;
  onUpdate?: (subscriptionId: string) => void;
  className?: string;
  showActions?: boolean;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onCancel,
  onReactivate,
  onUpdate,
  className = '',
  showActions = true,
}) => {
  const getStatusColor = (status: SubscriptionStatus): string => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case SubscriptionStatus.TRIALING:
        return 'bg-blue-100 text-blue-800';
      case SubscriptionStatus.PAST_DUE:
        return 'bg-yellow-100 text-yellow-800';
      case SubscriptionStatus.CANCELED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isActive =
    subscription.status === SubscriptionStatus.ACTIVE ||
    subscription.status === SubscriptionStatus.TRIALING;
  const isCanceled = subscription.status === SubscriptionStatus.CANCELED;
  const willCancel = subscription.cancelAtPeriodEnd;

  return (
    <div
      className={`bg-white rounded-lg shadow-md border border-gray-200 p-6 ${className}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {subscription.planName || 'Subscription'}
          </h3>
          <div className="flex items-center mt-1">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}
            >
              {formatSubscriptionStatus(subscription.status)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(subscription.amount || 0, subscription.currency)}
          </div>
          <div className="text-sm text-gray-500">
            per {subscription.interval}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Current Period:</span>
          <span className="text-sm text-gray-900">
            {formatDate(subscription.currentPeriodStart)} -{' '}
            {formatDate(subscription.currentPeriodEnd)}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-sm text-gray-500">Next Billing:</span>
          <span className="text-sm text-gray-900">
            {willCancel ? (
              <span className="text-red-600">
                Cancels {formatDate(subscription.currentPeriodEnd)}
              </span>
            ) : (
              formatDate(subscription.currentPeriodEnd)
            )}
          </span>
        </div>

        {subscription.trialEnd && (
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Trial Ends:</span>
            <span className="text-sm text-gray-900">
              {formatDate(subscription.trialEnd)}
            </span>
          </div>
        )}
      </div>

      {/* Warning Messages */}
      {willCancel && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex">
            <svg
              className="w-5 h-5 text-red-400 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-red-800">
                Subscription will be canceled
              </h4>
              <p className="text-sm text-red-700 mt-1">
                Your subscription will end on{' '}
                {formatDate(subscription.currentPeriodEnd)}. You'll retain
                access until then.
              </p>
            </div>
          </div>
        </div>
      )}

      {subscription.status === SubscriptionStatus.PAST_DUE && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <div className="flex">
            <svg
              className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-yellow-800">
                Payment failed
              </h4>
              <p className="text-sm text-yellow-700 mt-1">
                We couldn't process your payment. Please update your payment
                method.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-200">
          {isActive && !willCancel && (
            <>
              <button
                onClick={() => onUpdate?.(subscription.id)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
              >
                Update Plan
              </button>
              <button
                onClick={() => onCancel?.(subscription.id)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
            </>
          )}

          {willCancel && (
            <button
              onClick={() => onReactivate?.(subscription.id)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              Reactivate Subscription
            </button>
          )}

          {isCanceled && (
            <button
              onClick={() => onUpdate?.(subscription.id)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              Subscribe Again
            </button>
          )}

          {subscription.status === SubscriptionStatus.PAST_DUE && (
            <button
              onClick={() => onUpdate?.(subscription.id)}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              Update Payment Method
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
